import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  createModelPricing as createModelPricingApi,
  deleteModelPricing as deleteModelPricingApi,
  getAvailableLlmProviders,
  listModelPricing,
  updateModelPricing as updateModelPricingApi,
  type LlmProvider,
  type LlmProviderSpec,
  type ModelPricingCreate,
  type ModelPricingInDb,
  type ModelPricingUpdate,
} from '@/api';
import { logger } from '@/utils/logger';

export type AvailableProvidersMap = { [key in LlmProvider]?: LlmProviderSpec };

export const useAdminModelPricingStore = defineStore('admin-model-pricing', () => {
  const rows = ref<ModelPricingInDb[]>([]);
  const availableProviders = ref<AvailableProvidersMap>({});
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  const hasRows = computed<boolean>(() => rows.value.length > 0);

  async function fetchRows(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const response = await listModelPricing();
      if (!response.data) {
        throw new Error('Failed to fetch model pricing');
      }
      rows.value = response.data;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to fetch model pricing';
      logger.error('Error fetching model pricing:', error_);
    } finally {
      loading.value = false;
    }
  }

  async function fetchAvailableProviders(): Promise<void> {
    try {
      const response = await getAvailableLlmProviders();
      if (response.data) {
        availableProviders.value = response.data;
      }
    } catch (error_) {
      logger.error('Error fetching available LLM providers:', error_);
    }
  }

  async function createRow(data: ModelPricingCreate): Promise<ModelPricingInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await createModelPricingApi({ body: data });
      if (!response.data) {
        throw new Error('Failed to create model pricing');
      }
      await fetchRows();
      return response.data;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to create model pricing';
      logger.error('Error creating model pricing:', error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function updateRow(
    uid: string,
    data: ModelPricingUpdate
  ): Promise<ModelPricingInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await updateModelPricingApi({ path: { uid }, body: data });
      if (!response.data) {
        throw new Error('Failed to update model pricing');
      }
      await fetchRows();
      return response.data;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to update model pricing';
      logger.error('Error updating model pricing:', error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function deleteRow(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      await deleteModelPricingApi({ path: { uid } });
      await fetchRows();
      return true;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to delete model pricing';
      logger.error('Error deleting model pricing:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  return {
    rows,
    availableProviders,
    loading,
    error,
    hasRows,
    fetchRows,
    fetchAvailableProviders,
    createRow,
    updateRow,
    deleteRow,
  };
});
